import { MyBusiness } from "@1games-ru/region-shared/dist/backend/requests/business";
import { EmployeeRole } from "@1games-ru/region-shared/dist/enums/business/employee";
import { useCallback, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BusinessMpProvider from "@/core/data-provider/data-mp/business/business-mp-provider";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider.ts";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import { clearOffer } from "@/domains/offer/slice";
import toast from "@/domains/toasts/helpers/toast";

export default function useOfferCreatorEmployee() {
  const { t, i18n } = useTranslation("offer");
  const dispatch = useAppDispatch();
  const [businesses, setBusinesses] = useState<MyBusiness[]>([]);
  const [pending, setPending] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await BusinessMpProvider.getMyBusinesses();

      if (result instanceof RpcMpProviderFailedResponse) {
        console.error(result.error?.message ?? result);
        return;
      }

      setBusinesses(result.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const close = useCallback(() => {
    dispatch(clearOffer());
    GameMpProvider.closeCurrentInterface(true);
  }, [dispatch]);

  const create = useCallback(
    async (businessId: number, characterId: number, role: EmployeeRole) => {
      setPending(true);
      try {
        const result = await BusinessMpProvider.addEmployee(businessId, characterId, role);

        if (result instanceof RpcMpProviderFailedResponse) {
          const code = result.error?.message;
          if (code && i18n.exists(`offer:offer.errors.${code}`)) {
            console.error(code);
            toast.error(t(`offer.errors.${code}`));
          } else {
            console.error(code ?? result);
            toast.error(t("offer.errors.createError"));
          }
          close();
          return;
        }

        toast.success(t("offer.toast.accepted"));
        close();
      } catch (e) {
        console.error(e);
        toast.error(t("offer.errors.createError"));
      } finally {
        setPending(false);
      }
    },
    [i18n, t, close],
  );

  useLayoutEffect(() => {
    load();
  }, [load]);

  return {
    businesses,
    create,
    close,
    pending,
    loading,
  };
}
